import React from 'react';
import { View, TouchableOpacity, Text } from 'react-native';
import { Search, X, MapPin } from 'lucide-react-native';
import { FilterIcon } from '@/components/CardBadgeIcons';
import theme from '@/constants/colors';
import { useTranslation } from 'react-i18next';

interface ExploreSearchBarProps {
  onSearchPress: () => void;
  onFilterPress: () => void;
  activeFilterCount: number;
  searchLocationName?: string | null;
  onClearLocation?: () => void;
}

export default function ExploreSearchBar({
  onSearchPress,
  onFilterPress,
  activeFilterCount,
  searchLocationName,
  onClearLocation,
}: ExploreSearchBarProps) {
  const { t } = useTranslation();
  return (
    <View style={styles.container}>
      <View style={styles.searchRow}>
        <TouchableOpacity style={styles.searchBar} onPress={onSearchPress} activeOpacity={0.7}>
          <Search size={20} color={theme.backgroundPrimary} strokeWidth={2} />
          <View style={styles.searchTextContainer}>
            {searchLocationName ? (
              <View style={styles.locationContainer}>
                <MapPin size={14} color={theme.backgroundPrimary} strokeWidth={2.5} />
                <Text style={styles.locationText} numberOfLines={1}>{searchLocationName}</Text>
                {onClearLocation && (
                  <TouchableOpacity onPress={onClearLocation} style={styles.clearLocationButton} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                    <X size={14} color={theme.textMuted} />
                  </TouchableOpacity>
                )}
              </View>
            ) : (
              <Text style={styles.searchPlaceholder}>{t('explore.findTrailsOrLocations')}</Text>
            )}
          </View>
        </TouchableOpacity>
        <TouchableOpacity style={styles.filterButton} onPress={onFilterPress} activeOpacity={0.7}>
          <FilterIcon size={18} color={theme.accentPrimary} />
          {activeFilterCount > 0 && (
            <View style={styles.filterBadge}>
              <Text style={styles.filterBadgeText}>{activeFilterCount}</Text>
            </View>
          )}
        </TouchableOpacity>
      </View>
    </View>
  );
}

const styles = {
  container: {
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  searchRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 10,
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    backgroundColor: theme.textPrimary,
    borderWidth: 1,
    borderColor: theme.borderSubtle,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderRadius: 28,
    gap: 10,
  },
  searchTextContainer: {
    flex: 1,
  },
  searchPlaceholder: {
    fontSize: 15,
    color: theme.textMuted,
  },
  locationContainer: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    gap: 6,
  },
  locationText: {
    fontSize: 15,
    color: theme.backgroundPrimary,
    fontWeight: '600' as const,
    flex: 1,
  },
  clearLocationButton: {
    padding: 2,
  },
  filterButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: theme.backgroundSecondary,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    position: 'relative' as const,
  },
  filterBadge: {
    position: 'absolute' as const,
    top: -4,
    right: -4,
    minWidth: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: theme.accentPrimary,
    justifyContent: 'center' as const,
    alignItems: 'center' as const,
    paddingHorizontal: 4,
  },
  filterBadgeText: {
    fontSize: 10,
    color: theme.backgroundPrimary,
    fontWeight: '700' as const,
  },
};